import { createContext, useContext, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { normalizePath, pageMeta } from '@/lib/paths';

type RouterContextValue = {
  path: string;
  navigate: (to: string) => void;
};

const RouterContext = createContext<RouterContextValue | null>(null);

type RouterProps = {
  initialPath: string;
  children: ReactNode;
};

export function Router({ initialPath, children }: RouterProps) {
  const [path, setPath] = useState(() => normalizePath(initialPath));

  useEffect(() => {
    const onPop = () => {
      setPath(normalizePath(window.location.pathname));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const meta = pageMeta(path);
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
  }, [path]);

  const value = useMemo<RouterContextValue>(
    () => ({
      path,
      navigate: (to) => {
        const next = normalizePath(to);
        if (next === path) {
          return;
        }
        window.history.pushState({}, '', next);
        setPath(next);
      },
    }),
    [path],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new Error('useRouter must be used inside Router');
  }
  return ctx;
}

type AppLinkProps = {
  href: string;
  children: ReactNode;
  className?: string;
  external?: boolean;
};

export function AppLink({ href, children, className, external }: AppLinkProps) {
  const router = useRouter();

  if (external || href.startsWith('http') || href.startsWith('mailto:')) {
    return (
      <a className={className} href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  const current = router.path === normalizePath(href);

  function onClick(event: MouseEvent<HTMLAnchorElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    router.navigate(href);
  }

  return (
    <a className={className} href={href} aria-current={current ? 'page' : undefined} onClick={onClick}>
      {children}
    </a>
  );
}
